import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'outline' | 'light' | 'ghost-light'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: Variant
    className?: string
    external?: boolean
    size?: 'md' | 'lg'
}

const VARIANTS: Record<Variant, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-navy shadow-sm',
    secondary: 'bg-gold text-navy-deep hover:bg-[#f0b84a] shadow-sm',
    outline: 'border-2 border-primary text-primary hover:bg-secondary',
    light: 'bg-white text-navy hover:bg-sand shadow-sm',
    'ghost-light': 'border-2 border-white/70 text-white hover:bg-white/10'
}

export const buttonClasses = (variant: Variant = 'primary', size: 'md' | 'lg' = 'md'): string => cn(
    'group inline-flex items-center justify-center gap-3 rounded-xl font-bold no-underline transition-all duration-200 hover:-translate-y-0.5 focus-visible:-translate-y-0.5',
    size === 'lg' ? 'min-h-16 px-7 text-lg' : 'min-h-[3.75rem] px-6 text-base',
    VARIANTS[variant]
)

const ButtonLink: FC<ButtonLinkProps> = ({ href, children, variant = 'primary', className, external, size = 'md' }): ReactNode => {
    const classes = cn(buttonClasses(variant, size), className)

    if (external || href.startsWith('tel:') || href.startsWith('mailto:')) {
        return (
            <a
                href={href}
                className={classes}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {children}
        </Link>
    )
}

export default ButtonLink
